import { useMemo, useState } from 'react';
import { categories, feed, hotList, type GossipItem } from '@/mocks/gossip';
import ArticleModal from './ArticleModal';

const PAGE_SIZE = 4;

export default function FeedSection() {
  const [active, setActive] = useState('全部');
  const [keyword, setKeyword] = useState('');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [current, setCurrent] = useState<GossipItem | null>(null);

  const list = useMemo(() => {
    let rows = feed;
    if (active !== '全部') rows = rows.filter((r) => r.category === active);
    if (keyword.trim()) {
      rows = rows.filter((r) => r.title.includes(keyword.trim()) || r.summary.includes(keyword.trim()));
    }
    return rows;
  }, [active, keyword]);

  const shown = list.slice(0, visible);

  const changeCategory = (c: string) => {
    setActive(c);
    setVisible(PAGE_SIZE);
  };

  return (
    <section className="bg-background-50 py-14 md:py-20" id="feed">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-2 text-center">
          <span className="text-sm font-semibold text-primary-500">REAL-TIME · 实时爆料</span>
          <h2 className="font-display text-3xl text-foreground-950 md:text-4xl">实时热门爆料</h2>
          <p className="mx-auto max-w-xl text-sm text-foreground-600 md:text-base">
            打开吃瓜cb，热门爆料实时更新，端好你的饭碗，瓜料已经热好上桌。
          </p>
        </div>

        {/* 分类 + 搜索 */}
        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-start">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => changeCategory(c)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active === c
                    ? 'bg-primary-500 font-semibold text-white'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setVisible(PAGE_SIZE);
              }}
              placeholder="搜个瓜，关键词试试"
              className="w-full rounded-full border border-background-200 bg-background-100 py-2.5 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
            />
          </div>
        </div>

        {/* 主内容 + 侧边热榜 */}
        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
          {/* 爆料列表 */}
          <div className="flex flex-col gap-4">
            {shown.map((item) => (
              <article
                key={item.id}
                className="group flex cursor-pointer gap-4 rounded-2xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200 hover:bg-background-100 md:gap-5 md:p-4"
                onClick={() => setCurrent(item)}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  className="h-24 w-28 shrink-0 rounded-xl object-cover transition-transform duration-300 group-hover:scale-[1.03] sm:h-28 sm:w-40"
                />
                <div className="flex min-w-0 flex-1 flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-800">
                      {item.category}
                    </span>
                    <span className="text-xs text-foreground-500">{item.time}</span>
                    <span className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-eye-line"></i>
                      {item.views}
                    </span>
                    {item.tag === '热搜爆' && (
                      <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-800">
                        <i className="ri-fire-fill"></i>
                        热搜爆
                      </span>
                    )}
                  </div>
                  <h3 className="mt-2 line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                    {item.title}
                  </h3>
                  <p className="mt-1 hidden text-sm text-foreground-600 sm:line-clamp-1">
                    {item.summary}
                  </p>
                  <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-primary-600">
                    边吃边看完整瓜
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </div>
                </div>
              </article>
            ))}

            {shown.length === 0 && (
              <div className="rounded-2xl border border-dashed border-background-300 bg-background-100 py-16 text-center text-foreground-500">
                <i className="ri-emotion-sad-line text-3xl"></i>
                <p className="mt-2 text-sm">这个分类暂时没有匹配的瓜，换个关键词再试试。</p>
              </div>
            )}

            {visible < list.length && shown.length > 0 && (
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="mx-auto mt-2 flex items-center gap-2 rounded-full border border-primary-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-500 hover:text-white"
              >
                查看更多瓜料
                <i className="ri-arrow-down-s-line"></i>
              </button>
            )}
          </div>

          {/* 今日热度榜 */}
          <aside id="hot" className="lg:scroll-mt-24">
            <div className="lg:sticky lg:top-24">
              <div className="flex items-center justify-between">
                <h4 className="flex items-center gap-2 font-display text-xl text-foreground-950">
                  <i className="ri-fire-line text-accent-500"></i>
                  今日热度榜
                </h4>
                <span className="flex items-center gap-1 text-xs text-accent-600">
                  <i className="ri-flashlight-fill"></i>
                  实时
                </span>
              </div>

              <ol className="mt-4 flex flex-col gap-3">
                {hotList.map((h) => (
                  <li
                    key={h.rank}
                    className="rounded-xl border border-background-200 bg-background-100/70 p-3 transition-colors hover:border-accent-300"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-display text-base ${
                          h.rank <= 3
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-200 text-foreground-500'
                        }`}
                      >
                        {h.rank}
                      </span>
                      <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground-800">
                        {h.title}
                      </p>
                      <i
                        className={`text-base ${
                          h.trend === 'up'
                            ? 'ri-arrow-up-line text-accent-600'
                            : h.trend === 'down'
                            ? 'ri-arrow-down-line text-secondary-500'
                            : 'ri-loader-2-line text-foreground-300'
                        }`}
                      ></i>
                    </div>
                    <div className="mt-2 flex items-center gap-2 pl-10">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-200">
                        <div
                          className="heat-fill h-full rounded-full bg-gradient-to-r from-accent-400 to-primary-500"
                          style={{ width: `${h.heat}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-semibold text-foreground-500">{h.heat}</span>
                    </div>
                  </li>
                ))}
              </ol>

              <a
                href="#real"
                className="mt-4 flex items-center justify-center gap-1 rounded-xl bg-secondary-100 py-3 text-sm font-semibold text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                去看看真香现场
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>

      <ArticleModal item={current} onClose={() => setCurrent(null)} />
    </section>
  );
}